import { Flex, useMantineTheme } from '@mantine/core';
import debounce from 'debounce-promise';
import { when } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  DisplayCreative,
  Goal as GoalModel,
  TComparison,
  TTDCampaign,
} from '@feathr/blackbox';
import type { ISuggestion } from '@feathr/components';
import {
  FormElement,
  InputElement,
  Label,
  SegmentedControl,
  Suggestions,
} from '@feathr/components';
import { useStore } from '@feathr/extender/state';
import { DEFAULT_DEBOUNCE_WAIT } from '@feathr/hooks';

import { EditedGoalIdsContext } from '../EditedGoalIdsContext';
import {
  buildURLVisitPredicates,
  CREATIVE_URL_MATCH_ERROR,
  getEffectiveCreativeDestinationUrls,
  getGoalSegmentName,
  isGoalUrlSubjectToCreativeMatch,
  matchesCreativeDestination,
} from '../Goal.predicates';

import * as styles from './URLVisitGoal.css';

interface IProps {
  goal: GoalModel;
}

function URLVisitGoal({ goal }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const { Breadcrumbs, Campaigns, Creatives, Segments } = useStore();
  const editedGoalIds = useContext(EditedGoalIdsContext);

  const goalRef = useRef(goal);
  goalRef.current = goal;
  const [matchMode, setMatchMode] = useState<string>('eq');
  const [urlValue, setUrlValue] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [suggestions, setSuggestions] = useState<ISuggestion[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);

  const {
    spacing: { '2': gap },
  } = useMantineTheme();

  const debouncedGetSuggestions = useMemo(
    () =>
      debounce(async (query: string): Promise<void> => {
        try {
          const path = 'breadcrumbs/loc_url/values';
          const results = await Breadcrumbs.getSuggestions(path, query);
          const mapped: ISuggestion[] = results
            .filter((item) => item.value)
            .map((item) => ({ value: item.value, label: item.value }));
          setSuggestions(mapped);
        } catch {
          setSuggestions([]);
        } finally {
          setIsLoading(false);
        }
      }, DEFAULT_DEBOUNCE_WAIT),
    [Breadcrumbs],
  );

  const debouncedUpdateSegment = useMemo(
    () =>
      debounce(async (url: string, mode: TComparison): Promise<void> => {
        if (!url) {
          return;
        }

        const predicates = buildURLVisitPredicates(url, mode);
        const segmentName = getGoalSegmentName(goalRef.current, Campaigns);

        const result = await Segments.getOrCreateGoalSegment(predicates, segmentName);
        goalRef.current.set({ segment: result.segment.id });
      }, DEFAULT_DEBOUNCE_WAIT),
    [Campaigns, Segments],
  );

  useEffect(() => {
    async function loadExistingSegment(): Promise<void> {
      const segmentId = goal.get('segment');
      if (!segmentId) {
        return;
      }
      const segment = Segments.get(segmentId);
      await when(() => segment.isFetched);
      const predicates = segment.get('predicates');

      // No predicates when a value hasn't been set for the first time
      if (!predicates?.length) {
        return;
      }

      // Handle both flat (current) and nested (legacy) predicate structures
      const firstPredicate = predicates[0];
      const urlPredicate =
        firstPredicate?.attr_against === 'loc_url'
          ? firstPredicate
          : firstPredicate?.group?.find((p) => p.attr_against === 'loc_url');
      if (urlPredicate?.value && typeof urlPredicate.value === 'string') {
        setUrlValue(urlPredicate.value);
        if (urlPredicate.comparison) {
          setMatchMode(urlPredicate.comparison);
        }
      }
    }

    loadExistingSegment();
  }, []);

  const data = [
    {
      label: t('Exact match'),
      value: 'eq',
    },
    {
      label: t('Starts with'),
      value: 'starts_with',
    },
  ];

  function handleInputChange(value: string = ''): void {
    setUrlValue(value);
    editedGoalIds.add(goal.id);
    setIsLoading(true);
    debouncedGetSuggestions(value);
    debouncedUpdateSegment(value, matchMode as TComparison);
  }

  function handleInputFocus(): void {
    setIsDropdownOpen(true);
    setIsLoading(true);
    debouncedGetSuggestions(urlValue);
  }

  function handleInputBlur(): void {
    setIsDropdownOpen(false);
  }

  function handleSuggestionClick(value: string): void {
    setUrlValue(value);
    editedGoalIds.add(goal.id);
    setIsDropdownOpen(false);
    debouncedUpdateSegment(value, matchMode as TComparison);
  }

  function handleChangeMatchMode(value: string): void {
    setMatchMode(value);
    editedGoalIds.add(goal.id);
    if (urlValue) {
      debouncedUpdateSegment(urlValue, value as TComparison);
    }
  }

  const campaignId = goal.get('parent');
  const campaign = campaignId ? Campaigns.get(campaignId) : undefined;
  const isAdCampaign = !!campaign?.isAdCampaign;

  const creativesResponse =
    isAdCampaign && campaignId
      ? Creatives.list({ filters: { _parent: campaignId, is_archived__ne: true } })
      : undefined;

  // While the creatives list is still loading we can't determine a match, so no
  // error is shown. Reading the observable load state keeps this observer
  // subscribed, so the error appears as soon as the fetch settles.
  const creativeDestinationUrls =
    creativesResponse?.hasLoadedOnce && !creativesResponse.isPending
      ? getEffectiveCreativeDestinationUrls(
          creativesResponse.models as DisplayCreative[],
          (campaign as TTDCampaign).get('destination_url'),
        )
      : [];

  // Shares the StepGoals gate's helpers (isGoalUrlSubjectToCreativeMatch +
  // matchesCreativeDestination), so the inline error and the save gate stay in sync.
  const urlMatchError =
    isGoalUrlSubjectToCreativeMatch(!!campaign?.isDraft, editedGoalIds.has(goal.id)) &&
    matchesCreativeDestination(urlValue, matchMode as TComparison, creativeDestinationUrls)
      ? t(CREATIVE_URL_MATCH_ERROR)
      : undefined;

  return (
    <Flex direction={'column'} gap={gap}>
      <Label margin={'condensed'}>{t('Target URL')}</Label>
      <Flex align={'flex-start'} gap={gap}>
        <SegmentedControl data={data} onChange={handleChangeMatchMode} value={matchMode} />
        <FormElement className={styles.inputFormElement} validationError={urlMatchError}>
          <div className={styles.inputWrapper} onBlur={handleInputBlur} onFocus={handleInputFocus}>
            <InputElement
              aria-label={t('URL input')}
              onChange={handleInputChange}
              placeholder={t('Start typing to search URLs')}
              value={urlValue}
              wrapperClassName={styles.input}
            />
            {isDropdownOpen && (
              <div className={styles.dropdown}>
                <Suggestions
                  isLoading={isLoading}
                  onClickSuggestion={handleSuggestionClick}
                  suggestions={suggestions}
                />
              </div>
            )}
          </div>
        </FormElement>
      </Flex>
    </Flex>
  );
}

export default observer(URLVisitGoal);
